import type {Page} from './page'
import type {ErrorLogRef, Instant, UUID} from './common'

/* eslint-disable i18next/no-literal-string */
export const NotificationEventType = {
    ACCESS_REQUEST_CREATED: 'ACCESS_REQUEST_CREATED',
    ACCESS_REQUEST_APPROVED: 'ACCESS_REQUEST_APPROVED',
    ACCESS_REQUEST_REJECTED: 'ACCESS_REQUEST_REJECTED',
    ACCESS_PROFILE_ASSIGNMENT_GRANTED: 'ACCESS_PROFILE_ASSIGNMENT_GRANTED',
    ACCESS_PROFILE_ASSIGNMENT_GRANTED_BY_RULE: 'ACCESS_PROFILE_ASSIGNMENT_GRANTED_BY_RULE',
    ACCESS_PROFILE_ASSIGNMENT_REVOKED: 'ACCESS_PROFILE_ASSIGNMENT_REVOKED',
    ACCESS_PROFILE_ASSIGNMENT_RESTORED: 'ACCESS_PROFILE_ASSIGNMENT_RESTORED',
    ACCESS_REQUEST_CANCELLED: 'ACCESS_REQUEST_CANCELLED',
    FUNCTION_GROUP_REQUEST_CREATED: 'FUNCTION_GROUP_REQUEST_CREATED',
    FUNCTION_GROUP_REQUEST_APPROVED: 'FUNCTION_GROUP_REQUEST_APPROVED',
    FUNCTION_GROUP_REQUEST_REJECTED: 'FUNCTION_GROUP_REQUEST_REJECTED',
    FUNCTION_GROUP_REQUEST_CANCELLED: 'FUNCTION_GROUP_REQUEST_CANCELLED',
    FUNCTION_GROUP_ASSIGNMENT_GRANTED: 'FUNCTION_GROUP_ASSIGNMENT_GRANTED',
    FUNCTION_GROUP_ASSIGNMENT_GRANTED_BY_RULE: 'FUNCTION_GROUP_ASSIGNMENT_GRANTED_BY_RULE',
    FUNCTION_GROUP_ASSIGNMENT_REVOKED: 'FUNCTION_GROUP_ASSIGNMENT_REVOKED',
    FUNCTION_GROUP_ASSIGNMENT_RESTORED: 'FUNCTION_GROUP_ASSIGNMENT_RESTORED',
    ACCESS_PROFILE_ASSIGNMENT_EXPIRING: 'ACCESS_PROFILE_ASSIGNMENT_EXPIRING',
    FUNCTION_GROUP_ASSIGNMENT_EXPIRING: 'FUNCTION_GROUP_ASSIGNMENT_EXPIRING',
    IDENTITY_SUSPENDED: 'IDENTITY_SUSPENDED',
    IDENTITY_TERMINATED: 'IDENTITY_TERMINATED',
    PROVISIONING_FAILED: 'PROVISIONING_FAILED',
    CERTIFICATION_CAMPAIGN_STARTED: 'CERTIFICATION_CAMPAIGN_STARTED',
    CERTIFICATION_ITEM_PENDING: 'CERTIFICATION_ITEM_PENDING',
    CERTIFICATION_ITEM_OVERDUE: 'CERTIFICATION_ITEM_OVERDUE',
    CERTIFICATION_CAMPAIGN_COMPLETED: 'CERTIFICATION_CAMPAIGN_COMPLETED',
    REPORT_DELIVERED: 'REPORT_DELIVERED',
    ESCALATION_REMINDER: 'ESCALATION_REMINDER',
    ESCALATION_FINAL_REMINDER: 'ESCALATION_FINAL_REMINDER',
    ESCALATION_ESCALATED_TO_MANAGER: 'ESCALATION_ESCALATED_TO_MANAGER',
    ESCALATION_ESCALATED_TO_PLATFORM_OWNER: 'ESCALATION_ESCALATED_TO_PLATFORM_OWNER',
    ESCALATION_CHAIN_CLOSED: 'ESCALATION_CHAIN_CLOSED',
} as const
/* eslint-enable i18next/no-literal-string */

export type NotificationEventType = (typeof NotificationEventType)[keyof typeof NotificationEventType]

/* eslint-disable i18next/no-literal-string */
export const NotificationStatus = {
    SENT: 'SENT',
    PENDING: 'PENDING',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
} as const
/* eslint-enable i18next/no-literal-string */

export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus]

/* eslint-disable i18next/no-literal-string */
export const NotificationTemplateCategory = {
    ACCESS_REQUEST: 'ACCESS_REQUEST',
    ACCESS_PROFILE_ASSIGNMENT: 'ACCESS_PROFILE_ASSIGNMENT',
    FUNCTION_GROUP_REQUEST: 'FUNCTION_GROUP_REQUEST',
    FUNCTION_GROUP_ASSIGNMENT: 'FUNCTION_GROUP_ASSIGNMENT',
    IDENTITY: 'IDENTITY',
    PROVISIONING: 'PROVISIONING',
    REHIRE: 'REHIRE',
    CERTIFICATION: 'CERTIFICATION',
    REPORT: 'REPORT',
    WORKFLOW: 'WORKFLOW',
} as const
/* eslint-enable i18next/no-literal-string */

export type NotificationTemplateCategory =
    (typeof NotificationTemplateCategory)[keyof typeof NotificationTemplateCategory]

export interface Notification {
    id: UUID
    eventType: NotificationEventType
    title: string
    message: string
    referenceId: UUID | null
    read: boolean
    createdAt: Instant
}

export interface NotificationWithData<T> extends Notification {
    data: T | null;
}

export interface AdminNotification extends Notification {
    recipientIdentityId: UUID
    notificationStatus: NotificationStatus
    errorMessage: string | null
    sentAt: Instant | null
    errorLogRef: ErrorLogRef | null
}

export type NotificationPage = Page<Notification>
export type AdminNotificationPage = Page<AdminNotification>

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const NotificationResponseFields = {
    id: 'id',
    eventType: 'eventType',
    title: 'title',
    message: 'message',
    referenceId: 'referenceId',
    read: 'read',
    createdAt: 'createdAt',
} as const

export const AdminNotificationResponseFields = {
    id: 'id',
    eventType: 'eventType',
    title: 'title',
    message: 'message',
    referenceId: 'referenceId',
    read: 'read',
    createdAt: 'createdAt',
    recipientIdentityId: 'recipientIdentityId',
    notificationStatus: 'notificationStatus',
    status: 'status',
    errorMessage: 'errorMessage',
    sentAt: 'sentAt',
    errorLogRef: 'errorLogRef',
} as const
/* eslint-enable i18next/no-literal-string */
